<template>
  <div id="app">
    <router-view/>
    <DuplicateCheckModal />

    <!-- New chat messages (bottom right) -->
    <ChatToast />

    <!-- Quick message button: tags the record on screen -->
    <QuickChat />

    <!-- Inactivity warning modal -->
    <div v-if="showWarningModal" class="idle-modal-backdrop">
      <div class="idle-modal">
        <h4>You're about to be logged out</h4>
        <p>You will be automatically signed out due to inactivity in
          {{ Math.round((inactivityLimit - warningDuration) / 1000) }} seconds.
        </p>
        <div class="idle-modal-actions">
          <button class="btn btn-primary" @click="stayLoggedIn">Stay Logged In</button>
          <button class="btn btn-danger" @click="forceLogout">Logout Now</button>
        </div>
      </div>
    </div>

    <!-- Pending authorization codes notification (Claims Verifier) -->
    <div v-if="showPendingAuthModal" class="pending-auth-modal-backdrop">
      <div class="pending-auth-modal">
        <div class="pending-auth-modal-icon"><i class="fe fe-shield"></i></div>
        <h4>Pending Authorization Codes</h4>
        <p>
          You have <strong>{{ pendingAuthCount }}</strong> authorization
          code{{ pendingAuthCount === 1 ? '' : 's' }} awaiting your review.
        </p>
        <div class="pending-auth-modal-actions">
          <button class="btn btn-outline-secondary" @click="dismissPendingAuthModal">Dismiss</button>
          <button class="btn btn-primary" @click="reviewPendingAuthCodes">Review Now</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatToast from "@/chat/components/ChatToast.vue";
import DuplicateCheckModal from "@/views/reports/components/DuplicateCheckModal.vue";
import QuickChat from "@/chat/components/QuickChat.vue";
import chat from "@/chat/store";

export default {
  components: { DuplicateCheckModal, ChatToast, QuickChat },
  data() {
    return {
      timeout: 6000,
      inactivityTimeout: null,
      warningTimeout: null,
      inactivityLimit: 6 * 60 * 60 * 1000, // 6 hours
      warningDuration: 60 * 1000, // show warning 1 minute before logout
      showWarningModal: false,
      pendingAuthCount: 0,
      showPendingAuthModal: false,
      pendingAuthPollTimer: null,
    };
  },
  methods: {
    checkPendingAuthCodes() {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      const agency = JSON.parse(localStorage.getItem('agency') || 'null');
      if (!user || !agency || user.job_title !== 'Claims Verifier') return;

      this.axios
        .get(`/api/v1/auth/authorization_code/${agency.id}`, {
          params: { status: 'pending', paginate_value: 1 },
        })
        .then((response) => {
          const total = response.data.meta ? response.data.meta.total : (response.data.total || 0);
          this.pendingAuthCount = total;
          this.showPendingAuthModal = total > 0;
        })
        .catch(() => {
          // Stay silent - this is a background check, not a user-triggered action.
        });
    },
    reviewPendingAuthCodes() {
      this.showPendingAuthModal = false;
      if (this.$route.path !== '/authorization-code') {
        this.$router.push('/authorization-code');
      }
    },
    dismissPendingAuthModal() {
      this.showPendingAuthModal = false;
    },
    resetTimer() {
      clearTimeout(this.inactivityTimeout);
      clearTimeout(this.warningTimeout);
      this.showWarningModal = false;

      const warnAt = Math.max(0, this.inactivityLimit - this.warningDuration);
      // schedule warning
      this.warningTimeout = setTimeout(() => {
        this.showWarning();
      }, warnAt);

      // schedule sign out
      this.inactivityTimeout = setTimeout(() => {
        this.signOut();
      }, this.inactivityLimit);
    },
    showWarning() {
      this.showWarningModal = true;
    },
    stayLoggedIn() {
      this.showWarningModal = false;
      this.resetTimer();
    },
    async signOut() {
      chat.stop();
      try {
        localStorage.removeItem('jwt');
        localStorage.removeItem('user');
      } catch (e) {
        // ignore
      }
      // Public pages (join telemedicine, marketplace, vendor sign-up) stay where they are.
      const needsLogin = this.$route && this.$route.matched.some((route) => route.meta && route.meta.requiresAuth);
      if (this.$router && needsLogin) this.$router.push('/');
    },
    forceLogout() {
      this.signOut();
    },
    addEventListeners() {
      const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
      events.forEach((evt) => window.addEventListener(evt, this.resetTimer));
    },
    removeEventListeners() {
      const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
      events.forEach((evt) => window.removeEventListener(evt, this.resetTimer));
      clearTimeout(this.inactivityTimeout);
      clearTimeout(this.warningTimeout);
    },
  },
  mounted() {
    chat.start(); // no-op unless signed in as a chat user
    this.addEventListeners();
    this.resetTimer();
    this.checkPendingAuthCodes();
    this.pendingAuthPollTimer = setInterval(this.checkPendingAuthCodes, 5 * 60 * 1000);
  },
  beforeDestroy() {
    this.removeEventListeners();
    clearInterval(this.pendingAuthPollTimer);
  },
};
</script>

<style>
.idle-modal-backdrop {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}
.idle-modal {
  background: #fff;
  padding: 20px;
  max-width: 420px;
  width: 90%;
  border-radius: 6px;
  text-align: center;
}
.idle-modal-actions { margin-top: 16px; display:flex; gap:12px; justify-content:center; }

.pending-auth-modal-backdrop {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1900;
}
.pending-auth-modal {
  background: #fff;
  padding: 28px 24px;
  max-width: 420px;
  width: 90%;
  border-radius: 10px;
  text-align: center;
}
.pending-auth-modal-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin: 0 auto 14px;
}
.pending-auth-modal-actions { margin-top: 18px; display:flex; gap:12px; justify-content:center; }
</style>
